import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  FormControlLabel,
  Switch,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Box,
  RadioGroup,
  Radio,
  FormHelperText,
} from "@mui/material";
import { useTranslation } from "react-i18next";

interface SectionFormProps {
  col: {
    accessorKey: string;
    sectionData: {
      label: string;
      levels?: boolean;
      groups?: boolean;
    };
    role: string;
  };
  inputForm: any;
  options?: any;
  optionsLevels?: any;
  setInputForm: React.Dispatch<React.SetStateAction<any>>;
  error?: any;
  errorMessage?: string;
  fullLevels?: any;
}

const SectionFormNewCampaign: React.FC<SectionFormProps> = ({
  col,
  inputForm,
  setInputForm,
  options = [],
  optionsLevels = [],
  error = false,
  errorMessage = "",
}) => {
  const { t } = useTranslation(["training-needs-campaign-page"]);
  const { label } = col.sectionData;
  const sectionState = inputForm[col.accessorKey] || {};
  const enabled = sectionState.enabled || false;
  const selectedOption = sectionState.selectedOption || t("add.all");
  const selectedLevels: any[] = sectionState.selectedLevels || ["allLevels"];
  const selectedGroups: any[] = sectionState.selectedGroups || ["all"];
  const [availableLevelsForHR, setAvailableLevelsForHR] =
    useState<any>(optionsLevels);

  const isLearnerSection = col.accessorKey == "section_learner";

  const groups = options;
  const levels = optionsLevels;

  const isExistManager = inputForm?.section_manager?.selectedLevels.length;

  useEffect(() => {
    if (isExistManager && label === "HR") {
      const managerLevels = inputForm?.section_manager?.selectedLevels || [
        "allLevels",
      ];

      const maxSelected = Math.max(
        ...managerLevels.map((level: any) => parseInt(level.name)),
      );

      const filteredManagerLevels = levels
        .filter((el: any) => parseInt(el.name) <= maxSelected)
        .sort((a: any, b: any) => parseInt(a.name) - parseInt(b.name));

      const validLevelsForHR = [
        ...(filteredManagerLevels.length ? filteredManagerLevels : levels),
      ];

      // Aggiorna `availableLevelsForHR` solo se necessario
      if (
        JSON.stringify(validLevelsForHR) !==
        JSON.stringify(availableLevelsForHR)
      ) {
        setAvailableLevelsForHR(validLevelsForHR);
      }

      // Non resettare `selectedLevels` se ci sono già valori validi
      const prevSelectedLevels = inputForm[col.accessorKey]?.selectedLevels || [
        "allLevels",
      ];
      const validSelectedLevels = prevSelectedLevels.filter((level: any) =>
        validLevelsForHR.some(
          (validLevel: any) => validLevel.name === level.name,
        ),
      );

      const checkValidSelectedLevels = validSelectedLevels.length
        ? validSelectedLevels
        : ["allLevels"];

      if (
        JSON.stringify(checkValidSelectedLevels) !==
        JSON.stringify(prevSelectedLevels)
      ) {
        setInputForm((prevState: any) => ({
          ...prevState,
          [col.accessorKey]: {
            ...prevState[col.accessorKey],
            selectedLevels: checkValidSelectedLevels, // Mantieni i livelli validi
          },
        }));
      }
    }

    if (!isExistManager) {
      setAvailableLevelsForHR(optionsLevels);
    }
  }, [
    levels,
    isExistManager,
    inputForm.section_manager?.selectedLevels,
    availableLevelsForHR,
  ]);

  const handleManagerLevelsChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    let value = event.target.value as string[];

    const isAllLevelsSelected = value[value.length - 1] == "allLevels";

    if (value.includes("allLevels")) {
      if (!isAllLevelsSelected) {
        value = value.filter((val) => val !== "allLevels");
      } else {
        value = ["allLevels"];
      }
    } else {
      value = value.filter((val) => val !== "allLevels");
    }

    setInputForm((prevState: any) => {
      const prevSectionState = prevState[col.accessorKey] || {};

      if (isAllLevelsSelected) {
        // Se "allLevels" è selezionato, deve essere l'unico valore
        return {
          ...prevState,
          [col.accessorKey]: {
            ...prevSectionState,
            ...sectionState,
            selectedLevels: ["allLevels"],
          },
          section_hr: {
            ...prevState?.section_hr,
            selectedLevels: ["allLevels"],
          },
        };
      }

      // Se "allLevels" non è selezionato, aggiorniamo i livelli selezionati
      const updatedLevels = value
        .map((desc) => {
          const level = levels.find((lvl: any) => lvl.name === desc);
          if (level) {
            return level;
          } else {
            console.error(`Livello non trovato: ${desc}`);
            return null;
          }
        })
        .filter((lvl) => lvl !== null);

      return {
        ...prevState,
        [col.accessorKey]: {
          ...prevSectionState,
          ...sectionState,
          selectedLevels: updatedLevels,
        },
        section_hr: {
          ...prevState?.section_hr,
          selectedLevels: ["allLevels"],
        },
      };
    });
  };

  const handleHRLevelsChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    let value = event.target.value as string[];

    const isAllLevelsSelected = value[value.length - 1] == "allLevels";

    if (value.includes("allLevels")) {
      if (!isAllLevelsSelected) {
        value = value.filter((val) => val !== "allLevels");
      } else {
        value = ["allLevels"];
      }
    } else {
      value = value.filter((val) => val !== "allLevels");
    }

    setInputForm((prevState: any) => {
      const prevSectionState = prevState[col.accessorKey] || {};

      if (isAllLevelsSelected) {
        return {
          ...prevState,
          [col.accessorKey]: {
            ...prevSectionState,
            ...sectionState,
            selectedLevels: ["allLevels"],
          },
        };
      }

      const updatedLevels = value
        .map((desc) => {
          const level = levels.find((lvl: any) => lvl.name === desc);

          if (level) {
            return level;
          } else {
            console.error(`Livello non trovato: ${desc}`);
            return null;
          }
        })
        .filter((lvl) => lvl !== null);
      return {
        ...prevState,
        [col.accessorKey]: {
          ...prevSectionState,
          ...sectionState,
          selectedLevels: updatedLevels,
        },
      };
    });
  };

  return (
    <Grid
      key={col.accessorKey}
      item
      xs={12}
      sm={12}
      paddingLeft={2}
      paddingBottom={isLearnerSection ? 2 : 6}
    >
      {/* Header and switch */}
      <Grid
        item
        xs={12}
        sm={12}
        paddingBottom={2}
        container
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h6" component="h6">
          {error && (
            <FormHelperText error disabled={error}>
              {errorMessage}
            </FormHelperText>
          )}
          {label} *
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={() => {
                const newEnabled = !enabled;
                setInputForm((prevState: any) => {
                  const prevSectionState = prevState[col.accessorKey] || {};
                  if (label == "HR")
                    return {
                      ...prevState,
                      [col.accessorKey]: {
                        ...prevSectionState,
                        enabled: newEnabled,
                        selectedOption: newEnabled ? "carica" : t("add.all"),
                        selectedLevels: newEnabled
                          ? selectedLevels
                          : ["allLevels"],
                        selectedGroups: newEnabled ? selectedGroups : ["all"],
                      },
                    };
                  else
                    return {
                      ...prevState,
                      [col.accessorKey]: {
                        ...prevSectionState,
                        enabled: newEnabled,
                        selectedOption: newEnabled ? "carica" : t("add.all"),
                        selectedLevels: newEnabled
                          ? selectedLevels
                          : ["allLevels"],
                        selectedGroups: newEnabled ? selectedGroups : ["all"],
                      },
                      section_hr: {
                        ...prevState?.section_hr, // Verifica che section_hr esista
                        selectedLevels: ["allLevels"], // Svuota i livelli per HR
                      },
                    };
                });
              }}
              required
            />
          }
          label={`${t("add.activate-switch")} ${label}`}
        />
      </Grid>

      <Grid container xs={12} sm={12} spacing={2}>
        {/* Select for levels */}
        <Grid item xs={12} sm={6}>
          {col.sectionData.levels && (
            <FormControl fullWidth disabled={!enabled}>
              <Select
                disabled={
                  (error && !optionsLevels) ||
                  (inputForm[col.accessorKey] &&
                    !inputForm[col.accessorKey].enabled)
                }
                error={error}
                labelId={`${label}-levels-label`}
                multiple
                value={selectedLevels.map((lvl) =>
                  lvl.name ? lvl.name : "allLevels",
                )} // Modifica per usare le descrizioni
                onChange={(e: any) => {
                  if (label === "Manager") {
                    handleManagerLevelsChange(e);
                  } else {
                    handleHRLevelsChange(e);
                  }
                }}
                renderValue={(selected) => {
                  if (selected.length == 0 || selected[0] == "allLevels") {
                    return <Typography>{t("add.all-levels")}</Typography>;
                  }
                  return (selected as any[]).join(", ");
                }}
                displayEmpty
              >
                <MenuItem
                  disabled={selectedLevels.includes("allLevels")}
                  value={"allLevels"}
                >
                  <Checkbox checked={selectedLevels.includes("allLevels")} />
                  <ListItemText primary={t("add.all-levels")} />
                </MenuItem>
                {label === "Manager"
                  ? levels.map((level: any) => (
                      <MenuItem key={level.name} value={level.name}>
                        <Checkbox
                          checked={selectedLevels.some(
                            (lvl: any) => lvl.name === level.name,
                          )}
                        />
                        <ListItemText primary={level.name} />
                      </MenuItem>
                    ))
                  : availableLevelsForHR.map((level: any) => (
                      <MenuItem key={level.name} value={level.name}>
                        <Checkbox
                          checked={selectedLevels.some(
                            (lvl: any) => lvl.name === level.name,
                          )}
                        />
                        <ListItemText primary={level.name} />
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>
          )}
        </Grid>

        {/* Options and input */}
        {isLearnerSection && (
          <Grid item xs={12} sm={12} container spacing={2}>
            <Grid item xs={12} sm={12} container spacing={2}></Grid>
          </Grid>
        )}

        {/* Select for groups */}
        {isLearnerSection && <Grid item xs={12} sm={6} spacing={2}></Grid>}

        <Grid item xs={12} sm={6} spacing={2}>
          {col.sectionData.groups && (
            <FormControl fullWidth>
              <Select
                labelId={`${label}-groups-label`}
                multiple
                value={selectedGroups}
                onChange={(e) => {
                  let selectedValues = e.target.value as any[];

                  const isAllValue =
                    selectedValues[selectedValues.length - 1] === "all";

                  if (selectedValues.includes("all")) {
                    if (!isAllValue) {
                      selectedValues = selectedValues.filter(
                        (val) => val !== "all",
                      );
                    } else {
                      selectedValues = ["all"];
                    }
                  } else {
                    selectedValues = selectedValues.filter(
                      (val) => val !== "all",
                    );
                  }

                  setInputForm({
                    ...inputForm,
                    role: label,
                    [col.accessorKey]: {
                      ...sectionState,
                      selectedGroups: selectedValues,
                    },
                  });
                }}
                renderValue={(selected) => {
                  if (selected.includes("all") || selected.length === 0) {
                    return <Typography>{t("add.all-groups")}</Typography>;
                  }
                  return selected.map((group) => group.name).join(", ");
                }}
                displayEmpty
                disabled={selectedOption !== "carica"}
              >
                {/* Opzione "All" */}

                <MenuItem
                  disabled={selectedGroups.includes("all")}
                  key={"all"}
                  value={"all"}
                >
                  <Checkbox checked={selectedGroups.includes("all")} />
                  <ListItemText primary={t("add.all-groups")} />
                </MenuItem>

                {/* Altri gruppi */}
                {groups.map((group: any) => (
                  <MenuItem key={group.id} value={group}>
                    <Checkbox
                      checked={
                        selectedGroups.includes("all")
                          ? false
                          : selectedGroups.some(
                              (selectedGroup) => selectedGroup.id === group.id,
                            )
                      }
                    />
                    <ListItemText primary={group.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SectionFormNewCampaign;
